/**
 * Shared constants for the STW Platform
 * Use ALL_CAPS naming convention for constants
 */

/**
 * Current engine version stamped on all newly created items and rooms.
 *
 * Version history:
 *   "0"     - Legacy (staging). World-space coordinates, baked scale in walls,
 *             offset = display model Y shift, pillars/floor/walls not Room children.
 *   "1.0.0" - Object hierarchy. Room-local coordinates, Room.scale applied via
 *             Object3D, offset = floor height, all surfaces are Room children.
 *   "1.1.0" - Room-shape modernization ahead of the scene/scene_object split. RoomObject.scale
 *             normalized from a uniform number to `{x,y,z}`; each `collisions[]`
 *             entry tagged with `asset_key` (mirrors its existing `type`).
 */
export const CURRENT_ENGINE_VERSION = '1.1.0';

/**
 * The engine version tier at which data becomes scene-shaped (lives in `scene`/`scene_object`
 * rather than `room`/`room_object`) instead of merely room-shape-modernized. Today this is the
 * same value as `CURRENT_ENGINE_VERSION` — 1.1.0 is both "the newest tier" and "the split
 * boundary" — but they're separate concepts that only coincide because no scene-shaped version
 * bump has happened yet. Compare against this constant (not `CURRENT_ENGINE_VERSION`) anywhere
 * the question is "has this data been split into scene tables", e.g.
 * `frontend/src/pages/admin/import.vue`'s `isSceneTier`.
 *
 * NEEDS MODULARITY DOWN THE LINE: `shared-utils/src/engine-migration` only has a registry for
 * room-shaped migrations (`ROOM_MIGRATIONS`, walked generically by `migrateRoomData()`) — there is
 * no equivalent for evolving `scene`/`scene_object`'s shape once `CURRENT_ENGINE_VERSION` moves
 * past this constant. `Scene` doesn't even have an `engine_version` column yet. The room-side
 * registry pattern (versioned step files + a generic walker) should be mirrored for scenes before
 * the first scene-shaped migration is needed, rather than one-off patching `convertRoomToScene()`
 * or the admin import tool.
 */
export const SCENE_SPLIT_VERSION = '1.1.0';

/**
 * Regex pattern for validating model type formats
 */
export const MODEL_TYPE_PATTERN = '^(glb|ply|ksplat)$';

/**
 * Default thumbnail URL used when no custom thumbnail is available
 */
export const DEFAULT_THUMBNAIL_URL =
  'https://static.scan-the-world.com/assets/DEFAULT_THUMBNAIL.jpg';

/**
 * Default model URL used for sample items
 */
export const DEFAULT_MODEL_URL =
  'https://static.scan-the-world.com/assets/DEFAULT_MODEL.glb';

/**
 * Static asset URLs - used in multiple components
 */
export const STW_LOGO_WHITE_BG_URL =
  'https://static.scan-the-world.com/assets/STW_WHITE_BG.png';
export const STW_LOGO_BLACK_BG_URL =
  'https://static.scan-the-world.com/assets/STW_BLACK_BG.png';
export const STW_LOGO_WHITE_URL =
  'https://static.scan-the-world.com/assets/STW_WHITE.png';
export const STW_LOGO_BLACK_URL =
  'https://static.scan-the-world.com/assets/STW_BLACK.png';
export const LARGE_HKSTP_LOGO_URL =
  'https://storage.googleapis.com/static.scan-the-world.com/assets/large_HKSTP_logo.png';

/**
 * External service URLs - used in multiple backend locations
 */
export const KIRI_API_BASE_URL = 'https://api.kiriengine.app/api/v1';
export const KIRI_VIDEO_UPLOAD_URL = `${KIRI_API_BASE_URL}/open/3dgs/video`;
export const KIRI_IMAGE_UPLOAD_URL = `${KIRI_API_BASE_URL}/open/3dgs/image`;
export const MESHY_API_BASE_URL = 'https://api.meshy.ai/openapi/v1';
export const MESHY_IMAGE_TO_3D_URL = `${MESHY_API_BASE_URL}/image-to-3d`;
export const STRIPE_BILLING_PORTAL_URL =
  'https://api.stripe.com/v1/billing_portal/sessions';

/**
 * Frequently used external links
 */
export const SHOPIFY_APP_URL = 'https://apps.shopify.com/3d_scan_products';

/**
 * Development environment defaults
 */
export const DEFAULT_API_URL = 'http://localhost:8080/v1';
export const FIREBASE_AUTH_EMULATOR_HOST = 'localhost:9099';
export const FIREBASE_STORAGE_EMULATOR_HOST = 'localhost:9199';

/**
 * User avatar fallback URL
 */
export const DEFAULT_USER_AVATAR_URL =
  'https://static.scan-the-world.com/assets/DEFAULT_THUMBNAIL.jpg';

/**
 * Avatar upload constraints
 */
export const AVATAR_MAX_SIZE = 2 * 1024 * 1024; // 2MB
export const AVATAR_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

/**
 * Firebase Storage infrastructure constants
 */
export const UNIFIED_BUCKET_NAME = 'bucket.scan-the-world.com';
export const BUCKET_CUSTOM_DOMAIN = 'https://bucket.scan-the-world.com/';
export const TEMP_BUCKET_NAME = 'stw-temp-bucket';

/**
 * View tracking types for item analytics
 */
export enum VIEW_TYPE {
  ITEM_PAGE = 'item_page',
  EMBED = 'embed',
  VIEWER = 'viewer',
}

/**
 * View tracking types for scene analytics
 */
export enum SCENE_VIEW_TYPE {
  SCENE_PAGE = 'scene_page',
  EMBED = 'embed',
}

/**
 * Feedback source platforms
 */
export const FEEDBACK_SOURCE = {
  SHOPIFY: 'shopify',
  WORDPRESS: 'wordpress',
  STW_PLATFORM: 'stw_platform',
} as const;

export type FeedbackSource =
  (typeof FEEDBACK_SOURCE)[keyof typeof FEEDBACK_SOURCE];

/**
 * Feedback types/reasons
 */
export const FEEDBACK_TYPE = {
  UNINSTALL: 'uninstall',
  UNSUBSCRIBE: 'unsubscribe',
  CHURN: 'churn',
  FUTURE_REQUEST: 'future_request',
  GENERAL: 'general',
} as const;

export type FeedbackType = (typeof FEEDBACK_TYPE)[keyof typeof FEEDBACK_TYPE];
